// Libs
import { useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { IoMdEye } from "react-icons/io";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import * as Yup from "yup";
// UI-Components
import {
  OutletHeading,
  OutletSubHeading,
} from "../../UI-Components/OutletWrapper";
import { Label } from "../../UI-Components/Label";
import { Input } from "../../UI-Components/Input";
import { Button } from "../../UI-Components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogIcon,
  DialogTitle,
} from "../../UI-Components/Dialog";
import SuccessIcon from "../../UI-Components/SuccessIcon";
import { DynamicListingTable } from "../../UI-Components/DynamicListingTable";
import Spinner from "../../UI-Components/Spinner";
// Types
import {
  CheckedStatus,
  PreAuthData,
  StatusCheckForm,
} from "../../Libs/Types/StatusCheck";
import {
  FailureResponseWrapper,
  GetPreAuthAPI_Response,
  GetSatusByReferenceAPI_Response,
  SuccessResponseWrapper,
} from "../../Libs/Types/API-Responses";
// Endpoints
import {
  apiGetPreAuthList,
  apiGetPreAuthStatusByReferenceNumber,
} from "../../Endpoints/Preauthorizations";

export default function StatusCheck() {
  const initialValues: StatusCheckForm = {
    referenceNumber: "",
  };

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [preAuthData, setPreAuthData] = useState<PreAuthData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [preAuthStatus, setPreAuthStatus] = useState<CheckedStatus>({
    status: "",
    description: "",
  });
  const replaceString: string = "N/A";
  const pageSize: number = 6;
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending";
  } | null>(null);

  const requestSort = (key: string) => {
    let direction: "ascending" | "descending" = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Filtered Payers
  const filteredPreAuth: PreAuthData[] = preAuthData.filter((preAuth) => {
    const matchesSearch =
      preAuth?.reference_number?.toString().toLowerCase() ||
      preAuth?.unique_id?.toString().toLowerCase();

    return matchesSearch;
  });

  function getNestedValue(obj: PreAuthData, path: string) {
    return path.split(".").reduce((acc: any, part: any) => acc?.[part], obj);
  }

  const sortedPreAuth: PreAuthData[] = useMemo(() => {
    if (!sortConfig) return filteredPreAuth;

    return [...filteredPreAuth].sort((a, b) => {
      const aValue = String(getNestedValue(a, sortConfig.key) ?? "");
      const bValue = String(getNestedValue(b, sortConfig.key) ?? "");

      if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }, [filteredPreAuth, sortConfig]);

  // Pagination calculations
  const totalPages: number = Math.ceil(sortedPreAuth.length / pageSize);

  // Setting Paginated Data
  const paginatedPreAuth: PreAuthData[] = sortedPreAuth.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Handle Pagination
  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle Dialog Close
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  // API Response Handling
  const { mutateAsync: getPreAuthStatus, isPending: isPreAuthStatusPending } =
    useMutation({
      mutationFn: apiGetPreAuthStatusByReferenceNumber,
      retry: 1,
      onSuccess: async (data) => {
        const respData: SuccessResponseWrapper<GetSatusByReferenceAPI_Response> =
          data.data;
        setPreAuthStatus({
          status: respData?.data?.status ?? replaceString,
          description: respData?.data?.description ?? replaceString,
        });
        setDialogOpen(true);
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error?.response?.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
      },
      throwOnError: false,
    });

  // Handle Status Check Form Submit
  const handleStatusCheckForm = async (values: StatusCheckForm) => {
    await getPreAuthStatus(values.referenceNumber);
  };
  const statusValidationSchema = Yup.object().shape({
    referenceNumber: Yup.string()
      .min(2, "Reference Number must be at least 2 characters")
      .required("Reference Number is required"),
  });

  // API Response Handling
  const { mutateAsync: getPreAuthData, isPending: isPreAuthDataPending } =
    useMutation({
      mutationFn: apiGetPreAuthList,
      retry: 1,
      onSuccess: async (data) => {
        const respData: SuccessResponseWrapper<GetPreAuthAPI_Response[]> =
          data.data;
        const mappedData: PreAuthData[] = respData.data.map((preAuth) => ({
          patient_id: preAuth?.patient_id ?? replaceString,
          unique_id: preAuth?.unique_patient_id ?? replaceString,
          hcpcs_code: preAuth?.hcpcs_code ?? replaceString,
          reference_number: preAuth?.reference_number ?? replaceString,
          status: preAuth?.status ?? replaceString,
        }));

        setPreAuthData(mappedData);
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error?.response?.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
      },
      throwOnError: false,
    });

  const columns = [
    { key: "unique_id", header: "Unique ID", sortable: true, width: "100px" },
    {
      key: "patient_id",
      header: "Patient ID",
      sortable: false,
      width: "100px",
    },
    {
      key: "reference_number",
      header: "Reference Number",
      sortable: true,
      width: "100px",
    },
    {
      key: "status",
      header: "Status",
      sortable: false,
      width: "100px",
      cellRenderer: (row: any) => (
        <span
          className={`px-1 ${
            row.status === "approved"
              ? "bg-green-200"
              : row.status === "pending"
              ? "bg-yellow-200"
              : "bg-red-200"
          } capitalize`}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "hcpcs_code",
      header: "HCPCS Code",
      sortable: false,
      width: "100px",
    },
  ];

  const actions: any = [
    {
      name: "View",
      icon: <IoMdEye />,
      callback: async (data: any) => {
        if (isPreAuthStatusPending) return;
        await getPreAuthStatus(data.reference_number);
      },
    },
  ];

  useEffect(() => {
    getPreAuthData();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="px-2">
        <OutletHeading>Status Check</OutletHeading>
        <OutletSubHeading className="mt-2">Check Status</OutletSubHeading>

        <Formik
          initialValues={initialValues}
          onSubmit={handleStatusCheckForm}
          validationSchema={statusValidationSchema}
        >
          {({ isValid }) => (
            <Form className="w-full">
              <div className="grid md:grid-cols-2 gap-x-4">
                <div className="my-1 md:col-span-2">
                  <Label
                    className="block text-primary text-xs my-2 font-semibold"
                    htmlFor="referenceNumber"
                  >
                    Enter Reference Number
                  </Label>
                  <div className="flex gap-4">
                    <div className="flex flex-col w-full">
                      <Field
                        as={Input}
                        className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                        type="text"
                        id="referenceNumber"
                        name="referenceNumber"
                        // required
                        // pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                        placeholder="Enter Reference Number"
                      />
                      <ErrorMessage
                        name="referenceNumber"
                        component="p"
                        className="text-red-500 text-sm"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full lg:w-1/4"
                      disabled={!isValid || isPreAuthStatusPending}
                    >
                      {isPreAuthStatusPending ? "Checking..." : "Check Status"}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>

        {isPreAuthDataPending ? (
          <div className="flex justify-center items-center gap-5 mt-10">
            <span>Loading...</span>
            <Spinner className="w-6 h-6 border-primary-btn border-t-white" />
          </div>
        ) : (
          <>
            <section className="mt-2 max-h-[45vh] overflow-y-auto">
              <DynamicListingTable
                data={paginatedPreAuth}
                columns={columns}
                sortConfig={sortConfig}
                onSort={requestSort}
                actions={actions}
              />
            </section>
            <section>
              <div className="flex justify-end mt-2 space-x-2">
                <div className="p-2 text-disable-secondary">
                  <span className="text-primary">{currentPage}</span>
                  <span>
                    -{currentPage * pageSize} of {totalPages} Pages
                  </span>
                </div>
                <Button
                  className="p-2"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  variant="outline"
                >
                  <IoIosArrowBack />
                </Button>

                <Button
                  className="p-2"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  variant="outline"
                >
                  <IoIosArrowForward />
                </Button>
              </div>
            </section>
          </>
        )}
        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-3xl">
            <DialogIcon>
              <SuccessIcon />
            </DialogIcon>
            <DialogHeader>
              <DialogTitle>Status check completed successfully</DialogTitle>
              <DialogDescription>
                Status: <strong>{preAuthStatus.status}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="whitespace-pre-wrap text-center text-primary text-sm">
              {preAuthStatus.description}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
